import {useEffect, useId, useRef, useState} from "react";
import type {KeyboardEvent} from "react";
import {Link} from "react-router-dom";
import {Squares2X2Icon} from "@heroicons/react/24/outline";
import {cn} from "../../../lib/utils.ts";
import {GO_TO_ITEMS, getGoToHref} from "./utils";
import type {GoToItemId} from "./utils";

interface GoToMenuProps {
    clientId: string | null;
    visibleItemIds: GoToItemId[];
}

const ITEM_CLASSES = "flex items-center gap-3 rounded-lg px-2 py-2 focus:bg-gray-50 focus:outline-none";

export function GoToMenu({clientId, visibleItemIds}: GoToMenuProps) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const menuId = useId();

    const close = (restoreFocus: boolean) => {
        setIsOpen(false);
        if (restoreFocus) triggerRef.current?.focus();
    };

    const focusableItems = () => Array.from(menuRef.current?.querySelectorAll<HTMLElement>("a[role='menuitem']") ?? []);

    useEffect(() => {
        if (isOpen) (focusableItems()[0] ?? menuRef.current)?.focus();
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        function onMouseDown(event: MouseEvent) {
            if (!containerRef.current?.contains(event.target as Node)) close(false);
        }

        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [isOpen]);

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!isOpen) return;
        const items = focusableItems();
        const index = items.indexOf(document.activeElement as HTMLElement);
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close(true);
        } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            items[Math.min(index + 1, items.length - 1)]?.focus();
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            items[Math.max(index - 1, 0)]?.focus();
        }
    };

    return (
        <div ref={containerRef} onKeyDown={onKeyDown} className="relative shrink-0">
            <button
                ref={triggerRef}
                type="button"
                aria-label="Ga naar"
                title="Ga naar"
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-controls={isOpen ? menuId : undefined}
                onClick={() => (isOpen ? close(false) : setIsOpen(true))}
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary"
            >
                <Squares2X2Icon className="h-5 w-5" aria-hidden="true"/>
            </button>

            {isOpen && (
                <div
                    ref={menuRef}
                    id={menuId}
                    role="menu"
                    aria-label="Ga naar"
                    tabIndex={-1}
                    className="absolute right-0 z-50 mt-2 w-[320px] max-w-[calc(100vw-2rem)] rounded-xl bg-white p-1.5 shadow-lg ring-1 ring-black/5 focus:outline-none"
                >
                    {visibleItemIds.map((id) => {
                        const item = GO_TO_ITEMS[id];
                        const content = (
                            <>
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-superliteprimary text-primary">
                                    <item.Icon className="h-5 w-5" aria-hidden="true"/>
                                </span>
                                <span className="min-w-0">
                                    <span className="block truncate text-sm font-medium text-gray-900">{item.title}</span>
                                    <span className="block truncate text-xs text-gray-500">{item.description}</span>
                                </span>
                            </>
                        );
                        return clientId ? (
                            <Link
                                key={id}
                                role="menuitem"
                                to={getGoToHref(clientId, id)}
                                onClick={() => close(false)}
                                className={cn(ITEM_CLASSES, "hover:bg-gray-50")}
                            >
                                {content}
                            </Link>
                        ) : (
                            <span
                                key={id}
                                role="menuitem"
                                aria-disabled="true"
                                title="Kies eerst een klant"
                                className={cn(ITEM_CLASSES, "cursor-not-allowed opacity-50")}
                            >
                                {content}
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
